import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { useT } from "@/i18n/LanguageContext";

export default function Loader({ onDone }) {
  const { t } = useT();
  // Play the cinematic entrance once per browser session only.
  const alreadySeen =
    typeof window !== "undefined" && sessionStorage.getItem("rs-seen-entrance") === "1";

  const [stage, setStage] = useState(0);
  const [hide, setHide] = useState(alreadySeen);

  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (alreadySeen) {
      onDone && onDone();
      return;
    }

    if (reduce) {
      sessionStorage.setItem("rs-seen-entrance", "1");
      setHide(true);
      onDone && onDone();
      return;
    }

    let t1, t2, t3;
    let cancelled = false;

    // Hold the loader until the custom 'Burnts' face has actually painted —
    // otherwise the hero may briefly render with the serif fallback. Capped
    // at 2500ms so a flaky CDN can never hang the entrance.
    const ensureFont = () => {
      if (typeof document === "undefined" || !document.fonts || !document.fonts.load) {
        return Promise.resolve();
      }
      const fontPromise = document.fonts
        .load('1em "Burnts"')
        .then(() => document.fonts.ready);
      const timeout = new Promise((resolve) => setTimeout(resolve, 2500));
      return Promise.race([fontPromise, timeout]);
    };

    ensureFont().then(() => {
      if (cancelled) return;
      t1 = setTimeout(() => setStage(1), 100);
      t2 = setTimeout(() => setStage(2), 1000);
      t3 = setTimeout(() => {
        sessionStorage.setItem("rs-seen-entrance", "1");
        setHide(true);
        onDone && onDone();
      }, 2900);
    });

    return () => {
      cancelled = true;
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [onDone, alreadySeen]);

  return (
    <AnimatePresence>
      {!hide && (
        <motion.div
          data-testid="cinematic-loader"
          initial={{ y: 0 }}
          exit={{ y: "-100%" }}
          transition={{ duration: 1.1, ease: [0.85, 0, 0.15, 1] }}
          className="fixed inset-0 z-[9990] bg-ink text-bone flex flex-col items-center justify-center overflow-hidden"
        >
          <div className="absolute top-8 left-8 font-mono text-[10px] tracking-[0.3em] uppercase opacity-70">
            {t("loader.archive_no")}
          </div>
          <div className="absolute top-8 right-8 font-mono text-[10px] tracking-[0.3em] uppercase opacity-70">
            {t("loader.entrance")}
          </div>

          <div className="relative overflow-hidden py-[0.18em]">
            <motion.h1
              initial={{ y: "110%" }}
              animate={{ y: stage >= 1 ? "0%" : "110%" }}
              transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
              className="font-burnts text-[18vw] leading-[1.05] tracking-tight py-[0.1em]"
            >
              Rushit
            </motion.h1>
          </div>
          <div className="relative overflow-hidden -mt-[3vw] py-[0.18em]">
            <motion.h1
              initial={{ y: "110%" }}
              animate={{ y: stage >= 1 ? "0%" : "110%" }}
              transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
              className="font-burnts text-[18vw] leading-[1.05] tracking-tight py-[0.1em]"
            >
              Shah<span className="text-crimson">.</span>
            </motion.h1>
          </div>

          <motion.div
            initial={{ width: 0 }}
            animate={{ width: stage >= 2 ? "40%" : "0%" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="h-px bg-bone mt-12 max-w-[600px]"
          />
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: stage >= 2 ? 1 : 0 }}
            transition={{ duration: 0.6 }}
            className="mt-6 font-mono text-[10px] tracking-[0.22em] uppercase"
          >
            {t("loader.tagline")}
          </motion.p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
